import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from 'hooks/auth'
import SignupForm, { IFormValues } from 'views/auth/components/SignupForm'
import { useApplicationStore } from 'hooks/application'
import { SIGN_IN } from 'routes'
import api, { APIError } from 'helpers/api'
import { isValidEmail } from 'helpers/utils'
import Button from 'components/button'
import Input from 'components/input'

const SignUp: React.FC = () => {
  const [state, setState] = useState(1)
  const { register } = useAuth()
  const { setSnackbarMessage } = useApplicationStore()
  const [email, setEmail] = useState<string>('')
  const [allowedMethods, setAllowedMethods] = useState<string[] | null>(null)
  const [loadingMethods, setLoadingMethods] = useState<boolean>(false)
  const [emailError, setEmailError] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    document.title = 'Morta | Sign Up'
  }, [])

  const showSignupMethods = allowedMethods !== null

  const handleEmailSubmit = async () => {
    if (!isValidEmail(email)) {
      setEmailError(true)
      return
    }
    setLoadingMethods(true)
    setError(null)

    try {
      const response = await api.getAllowedLoginMethods(email)
      setAllowedMethods(response.data.allowedMethods)
    } catch (e) {
      if (e instanceof APIError) {
        const message = e.message || "Morta couldn't check how you can sign up. Refresh the page to try again."
        const allMessages = e.errors
          ? e.errors.map((err) => (Array.isArray(err.messages) ? err.messages.join(', ') : '')).join(' ')
          : ''
        const errorMessage = allMessages || message
        setError(errorMessage)
      }

      setAllowedMethods(null)
    }
    setLoadingMethods(false)
  }

  const handleBackToEmail = () => {
    setAllowedMethods(null)
    setEmail('')
    setEmailError(false)
    setError(null)
  }

  const handleSubmitSignup = async (values: IFormValues) => {
    try {
      const payload = {
        name: values.name,
        email: values.email,
        password: values.password1
      }
      await register(payload)
      setState(2)
    } catch (e) {
      if (e instanceof APIError) {
        setSnackbarMessage({
          message: e.message,
          status: 'error',
          duration: 30000,
          action: (
            <a className="p-[5px] ml-2.5 bg-surface rounded-sm border border-surface" href={`/signin`}>
              Return To Sign In
            </a>
          )
        })
      }

      return
    }

    setState(2)
  }

  const VerificationSent = (
    <React.Fragment>
      <p>
        A verification email has been sent to your email address. Please click on the link in the email to verify your
        account.
      </p>
      <br />
      <p className="border-t border-divider mt-5 pt-5">
        Return to <Link to={SIGN_IN}>Sign In</Link>
      </p>
    </React.Fragment>
  )

  // Determine if we should say "Sign in" or "Sign up" based on allowed methods
  const isOAuthOrSSOOnly =
    allowedMethods &&
    allowedMethods.length === 1 &&
    (allowedMethods.includes('google') || allowedMethods.includes('microsoft') || allowedMethods.includes('saml'))

  const actionText = isOAuthOrSSOOnly ? 'Sign in' : 'Sign up'

  return (
    <div className="bg-really-light-blue flex flex-1 items-center justify-center overflow-hidden box-border h-screen w-full px-8">
      <div className="h-fit flex flex-col bg-surface border border-divider-strong p-8 shadow-heavy rounded text-body items-center w-[550px]">
        {state === 1 && !showSignupMethods && (
          <>
            <div className="flex flex-col gap-4 mb-4 w-full">
              <h1 className="mb-4 flex justify-center">Welcome to Morta</h1>
              <label htmlFor="email">Enter your email address</label>
            </div>

            <div className="w-full">
              <Input
                id="email"
                name="email"
                type="text"
                data-cy="email-input"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value)
                  setError(null)
                  setEmailError(false)
                }}
                onKeyPress={(event) => {
                  if (event.key === 'Enter') {
                    handleEmailSubmit()
                  }
                }}
                autoComplete="email"
              />
              {emailError && <p className="text-red mt-2.5 mb-2.5">This must be a valid email address</p>}
              {error && <p className="text-red mt-2.5 mb-2.5">{error}</p>}
              <Button
                className="mt-5 w-full text-[1rem]"
                onClick={handleEmailSubmit}
                disabled={!email}
                isLoading={loadingMethods}
                data-cy="continue-button"
              >
                Continue
              </Button>
            </div>
            <p className="border-t border-divider mt-5 pt-5 w-full">
              Already have an account? <Link to={SIGN_IN}>Sign In</Link>
            </p>
          </>
        )}

        {state === 1 && showSignupMethods && (
          <>
            <h1 className="mb-8 text-2xl">Welcome {email}</h1>
            <SignupForm
              onSubmit={handleSubmitSignup}
              allowedMethods={allowedMethods}
              initialEmail={email}
              actionText={actionText}
            />
            <br />
            <Link to="#" onClick={handleBackToEmail} className="pt-[10px] cursor-pointer text-left w-full">
              Use a different email address
            </Link>
          </>
        )}

        {state === 2 && VerificationSent}
      </div>
    </div>
  )
}

export default SignUp
